/**
 * THE ASCENDING / DESCENDING TOGGLE that every filter sheet's header carries (0.1.393).
 *
 * ⚠️ ONE COMPONENT, FOUR SHEETS — the maintainer asked for this button in Projects, Boxes, Items
 * and Outfits, which is four chances for the word, the arrow and the meaning to drift apart. It is
 * a FACT (what "ascending" looks like and says), and this repo hoists a fact at the first duplicate.
 *
 * ⚠️ IT SAYS WHAT THE ORDER IS, NOT WHAT THE TAP DOES. "Ascending ↑" means the list IS ascending;
 * pressing it makes the list descending. A button labelled with its own action ("Sort descending")
 * reads as the current state to half the people who see it, and this one lives beside a list whose
 * order they can check — so the state is what it must name. `aria-pressed` carries the same fact to
 * a screen reader without inventing a second vocabulary for it.
 */
export type SortDirection = 'asc' | 'desc';

export function SortDirectionToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: SortDirection;
  onChange: (next: SortDirection) => void;
  disabled?: boolean;
}): JSX.Element {
  const ascending = value === 'asc';
  return (
    <button
      type="button"
      className={ascending ? 'sort-dir' : 'sort-dir sort-dir-desc'}
      disabled={disabled}
      aria-pressed={!ascending}
      aria-label={ascending ? 'Order: ascending. Press to sort descending.' : 'Order: descending. Press to sort ascending.'}
      onClick={() => onChange(ascending ? 'desc' : 'asc')}
    >
      <span aria-hidden="true">{ascending ? '↑' : '↓'}</span> {ascending ? 'Ascending' : 'Descending'}
    </button>
  );
}

/**
 * Apply a direction to a comparison. Kept beside the toggle so a sheet never writes
 * `dir === 'desc' ? -n : n` by hand and gets the sign wrong in one of four places.
 */
export function directed(comparison: number, direction: SortDirection): number {
  return direction === 'asc' ? comparison : -comparison;
}
